import Cookies from 'js-cookie';
import React, { useCallback, useEffect, useState } from 'react';
import {
	BrowserRouter,
	createRoutesFromChildren,
	matchRoutes,
	Route,
	Routes,
	useLocation,
	useMatch,
	useNavigate,
	useNavigationType,
} from 'react-router-dom';
import { ThemeProvider as BootstrapThemeProvider } from 'react-bootstrap';

import {
	AccountProvider,
	AlertProvider,
	AnalyticsProvider,
	NavigationConfigProvider,
	ThemeProvider as MasterClientThemeProvider,
} from '@/contexts';

import config from '@/lib/config';
import { constructUrl } from '@/lib/utils';
import { COOKIES } from '@/lib/models';
import { accountService, navigationService, publicService } from '@/lib/services';
import { Alert, AsyncPage, PrivateRoute } from '@/components';

import { prefixes } from '@/jss/bootstrap';
import { useGlobalStyles } from '@/jss/hooks/use-global-styles';
import { useThemedBootstrapStyles } from '@/jss/hooks/use-themed-bootstrap-styles';

import { routes } from '@/routes';

import 'react-bootstrap-typeahead/css/Typeahead.css';
import 'react-datepicker/dist/react-datepicker.css';

import { useAccount, useNavigationConfig } from '@/hooks';
import { ErrorConfig, isSignedOutError } from '@/lib/http-client';

import * as Sentry from '@sentry/react';

// Wrapped by <Router>, allows use of 'react-router-dom' hooks
const RoutedApp = () => {
	const navigate = useNavigate();
	const { pathname } = useLocation();
	const isSignInRoute = !!useMatch({ path: '/sign-in' });
	const isPublicRoute = !!useMatch({ path: '/public/*' });
	const { account, setAccount } = useAccount();
	const { setNavigationConfig } = useNavigationConfig();
	const [appIsInitialized, setAppIsInitialized] = useState(false);

	const fetchData = useCallback(async () => {
		try {
			if (appIsInitialized) {
				return;
			}

			const path = window.location.pathname;
			const searchParams = new URLSearchParams(window.location.search);
			const accessTokenFromQuery = searchParams.get('X-Hackett-Access-Token');

			// The login token is an HttpOnly cookie, so JS can't check it. A hand-off token in the URL is turned into
			// that cookie by the backend.
			if (accessTokenFromQuery) {
				await publicService.createSession(accessTokenFromQuery).fetch();

				searchParams.delete('X-Hackett-Access-Token');

				try {
					await accountService.trackSignIn().fetch();
				} catch (_trackingError) {
					// Fail silently, allow app to load even if tracking call is unsuccessful.
				}
			}

			const targetUrl = constructUrl(path, {}, searchParams.toString());

			// Whether the user is signed in is known by asking for the account: 401 means no valid cookie.
			// allSettled: on a 401 the http client aborts the other request, which must not hide the 401
			const [accountResult, navigationResult] = await Promise.allSettled([
				accountService.getCurrentAccount().fetch(),
				navigationService.getNavigationItems().fetch(),
			]);

			if (accountResult.status === 'rejected' || navigationResult.status === 'rejected') {
				const reasons = [accountResult, navigationResult]
					.filter((result): result is PromiseRejectedResult => result.status === 'rejected')
					.map((result) => result.reason);
				const signedOutReason = reasons.find(isSignedOutError);

				// Another error, or just signed in and still not authenticated: show the error instead of looping
				// through the auth UI
				if (!signedOutReason || accessTokenFromQuery) {
					throw signedOutReason ?? reasons[0];
				}

				if (isPublicRoute || isSignInRoute) {
					setAppIsInitialized(true);
					return;
				}

				Cookies.set(COOKIES.TARGET_URL, targetUrl, {
					sameSite: 'strict',
				});

				if (config.HACKETT_ADVISORY_USE_REAL_AUTH === 'true') {
					window.location.href = constructUrl(config.HACKETT_ADVISORY_AUTH_UI_URL, {
						redirectUrl: encodeURIComponent(window.location.href),
						platform: 'hackett-connect',
					});
				} else {
					navigate('/sign-in', { replace: true });
				}
				return;
			}

			const accountResponse = accountResult.value;
			const navigationResponse = navigationResult.value;

			Sentry.setUser({ email: accountResponse.account.emailAddress });
			setAccount(accountResponse.account);
			setNavigationConfig(navigationResponse);

			if (!accountResponse.account.hasAdvisoryProfile) {
				navigate('/complete-profile', { replace: true });

				// Force user to complete their profile
				setAppIsInitialized(true);
				return;
			}

			if (accessTokenFromQuery) {
				navigate(targetUrl, { replace: true });
			}

			setAppIsInitialized(true);
		} catch (error) {
			// Do not throw if the error status is 503.
			// If an error is thrown here, The <AsyncPage/> below would prevent this component's children from rendering.
			// That means the <Maintenance/> component could never show if a 503 occurs during initialization.
			// The <Maintenance/> component is tied to the "/maintenance" route, and all routes are technically a chilren of this component
			if ((error as ErrorConfig)?.status === 503) {
				return;
			}

			throw error;
		}
	}, [appIsInitialized, isPublicRoute, isSignInRoute, navigate, setAccount, setNavigationConfig]);

	useEffect(() => {
		window.scrollTo(0, 0);
	}, [pathname]);

	return (
		<AnalyticsProvider account={account} appIsInitialized={appIsInitialized}>
			<AsyncPage fetchData={fetchData}>
				<Alert />

				<Routes>
					{routes.map((route, index) => {
						return <Route key={index} path={route.path} element={<route.header />} />;
					})}
				</Routes>

				<Routes>
					{routes.map((route, index) => {
						return (
							<Route
								key={index}
								path={route.path}
								element={
									route.private ? (
										<PrivateRoute>
											<route.main />
										</PrivateRoute>
									) : (
										<route.main />
									)
								}
							/>
						);
					})}
				</Routes>

				<Routes>
					{routes.map((route, index) => {
						return <Route key={index} path={route.path} element={<route.footer />} />;
					})}
				</Routes>
			</AsyncPage>
		</AnalyticsProvider>
	);
};

// Wrapped by various <ThemeProviders>, allows use of 'jss' hooks
const ThemedApp = () => {
	useGlobalStyles();
	useThemedBootstrapStyles();

	return (
		<BrowserRouter>
			<RoutedApp />
		</BrowserRouter>
	);
};

// Exported App, not wrapped by anything, can add various context providers here
const App = () => {
	return (
		<MasterClientThemeProvider>
			<BootstrapThemeProvider prefixes={prefixes}>
				<AccountProvider>
					<NavigationConfigProvider>
						<AlertProvider>
							<ThemedApp />
						</AlertProvider>
					</NavigationConfigProvider>
				</AccountProvider>
			</BootstrapThemeProvider>
		</MasterClientThemeProvider>
	);
};

Sentry.init({
	dsn: config.SENTRY_DSN,
	release: __VERSION__,
	integrations: [
		new Sentry.BrowserTracing({
			routingInstrumentation: Sentry.reactRouterV6Instrumentation(
				React.useEffect,
				useLocation,
				useNavigationType,
				createRoutesFromChildren,
				matchRoutes
			),
		}),
		// DO NOT USE, causes performance slow down
		// new Sentry.Replay(),
	],
	// Performance Monitoring
	tracesSampleRate: 1.0, // Capture 100% of the transactions
	// Session Replay
	replaysSessionSampleRate: 1.0, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
	replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
});

export default App;
